import {
  hasTeamAuthorizationPolicyOverrides as hasSharedTeamAuthorizationPolicyOverrides,
  parseTeamAuthorizationPolicy as parseSharedTeamAuthorizationPolicy,
  PERMISSION_DOMAIN_KEYS,
  PERMISSION_OPERATIONS,
  PERMISSION_ROLES,
  type PermissionDomainKey,
  type PermissionOperation,
  type PermissionRole,
  type RolePermissionOverride,
  serializeTeamAuthorizationPolicy as serializeSharedTeamAuthorizationPolicy,
  type TeamAuthorizationPolicyV1,
} from '@skop-app/lib-domain/authorization'
import type { Role } from '@skop-app/lib-domain/types'

import type { OrganizationMetadataEntry } from '@/utils/organizationMetadata'
import { ORG_METADATA_ALLOW_REFS_CREATION_KEY } from '@/services/reference/reference.metadata'

export const TEAM_AUTHORIZATION_POLICY_V1_KEY = 'TEAM_AUTHORIZATION_POLICY_V1'

export { PERMISSION_DOMAIN_KEYS, PERMISSION_OPERATIONS, PERMISSION_ROLES }

export type {
  PermissionDomainKey,
  PermissionOperation,
  PermissionRole,
  RolePermissionOverride,
  TeamAuthorizationPolicyV1,
}

export const CONFIGURABLE_PERMISSION_ROLES = ['USER'] as const
export type ConfigurablePermissionRole =
  (typeof CONFIGURABLE_PERMISSION_ROLES)[number]

export const PERMISSION_TRI_STATES = ['inherit', 'allow', 'deny'] as const
export type PermissionTriState = (typeof PERMISSION_TRI_STATES)[number]

export type ParsedTeamAuthorizationPolicy = {
  policy: TeamAuthorizationPolicyV1 | null
  source: 'none' | 'policy' | 'legacy'
  error: string | null
}

const hasOwnKeys = (value: Record<string, unknown>) =>
  Object.keys(value).length > 0

const pickConfigurablePolicyRoles = (
  policy: TeamAuthorizationPolicyV1 | null | undefined,
): TeamAuthorizationPolicyV1 | null => {
  if (!policy) return null

  const nextRoles: Partial<Record<PermissionRole, RolePermissionOverride>> = {}

  for (const role of CONFIGURABLE_PERMISSION_ROLES) {
    const override = policy.roles?.[role]
    if (override) {
      nextRoles[role] = override
    }
  }

  return {
    version: 1,
    ...(hasOwnKeys(nextRoles) ? { roles: nextRoles } : {}),
  }
}

export const parseTeamAuthorizationPolicy = (
  value: unknown,
): ParsedTeamAuthorizationPolicy => {
  const result = parseSharedTeamAuthorizationPolicy(value)

  return {
    policy: result.policy,
    source: value == null ? 'none' : 'policy',
    error: result.error,
  }
}

export const serializeTeamAuthorizationPolicy = (
  policy: TeamAuthorizationPolicyV1 | null | undefined,
): TeamAuthorizationPolicyV1 | null =>
  serializeSharedTeamAuthorizationPolicy(pickConfigurablePolicyRoles(policy))

export const hasTeamAuthorizationPolicyOverrides = (
  policy: TeamAuthorizationPolicyV1 | null | undefined,
) => hasSharedTeamAuthorizationPolicyOverrides(policy)

export const hasLegacyAdminPermissionOverrides = (
  policy: TeamAuthorizationPolicyV1 | null | undefined,
) => Boolean(policy?.roles?.ADMIN)

export const getOrganizationMetadataEntry = (
  metadata: OrganizationMetadataEntry[],
  key: string,
) => metadata.find((entry) => entry.key === key)

export const readTeamAuthorizationPolicyFromMetadata = (
  metadata: OrganizationMetadataEntry[],
): ParsedTeamAuthorizationPolicy => {
  const entry = getOrganizationMetadataEntry(
    metadata,
    TEAM_AUTHORIZATION_POLICY_V1_KEY,
  )
  if (!entry) {
    return {
      policy: null,
      source: 'none',
      error: null,
    }
  }

  return parseTeamAuthorizationPolicy(entry.value)
}

export const getLegacyRestrictReferencesOverride = ({
  metadata,
  role,
  hasPolicy,
}: {
  metadata: OrganizationMetadataEntry[]
  role?: Role
  hasPolicy: boolean
}) => {
  if (hasPolicy || role !== 'USER') return undefined

  const legacyEntry = getOrganizationMetadataEntry(
    metadata,
    ORG_METADATA_ALLOW_REFS_CREATION_KEY,
  )

  const restrictRefs =
    legacyEntry?.value === true || legacyEntry?.value === 'true'

  return restrictRefs
    ? ({
        version: 1,
        roles: {
          USER: {
            references: {
              canCreate: false,
              canUpdate: false,
              canDelete: false,
            },
          },
        },
      } satisfies TeamAuthorizationPolicyV1)
    : undefined
}
